/**
 * `CaseFileRow` — ONE file row, shared by BOTH tree surfaces.
 *
 * Extracted from `CaseFileNav.tsx` (which was 1 452 LOC, well past the repo's
 * ~500–700 guideline) so the classic zone tree and the consolidated
 * case-files section render the SAME row component. That sharing is the point:
 * the interaction model here — expand-on-select card, idle auto-collapse,
 * overlay locks, right-click menu, drag as BOTH a mention source and a
 * copy/move source, and the `sources/`-only copy/move gate — must never fork
 * between the two surfaces. It also keeps the import graph a DAG
 * (`CaseFileNav` → `CaseFilesSection` → `CaseFileRow`, and `CaseFileNav` →
 * `CaseFileRow`) rather than a cycle.
 *
 * Interaction model (unchanged by the extraction):
 *   - single-click / tap on a COLLAPSED row SELECTS it — it expands in place to
 *     show the full description + action buttons (`data-active`/`aria-current`
 *     track `useWorkspaceTreeSelectionStore.selectedPath`).
 *   - a click on the ALREADY-EXPANDED card — anywhere on it — opens the
 *     CONTENT VIEWER (operator 2026-07-30). Details/edit is the PENCIL's job;
 *     the card's [X] collapses it. A double-click degrades to the same viewer,
 *     which is why no click-timer disambiguation exists.
 *   - ⚖ …EXCEPT that selecting a row while the viewer pane is ALREADY OPEN also
 *     switches the pane to that file in the same click (operator 2026-08-11).
 *     The card still expands — it is where the actions live — but the second
 *     click is gone, because once the pane is open the user is switching what
 *     they read, not deciding whether to read it. Pane closed ⇒ two phases, as
 *     above. See `handleClick`.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  AudioLines,
  ClipboardCopy,
  Copy,
  CornerUpRight,
  Download,
  FileSearch,
  Eye,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  HelpCircle,
  MoveRight,
  AlertTriangle,
  Pencil,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";
import { useToastStore } from "@/stores/useToastStore";
import { useFileDownload } from "@/hooks/useFileDownload";
import { useWorkspaceTreeSelectionStore } from "@/stores/useWorkspaceTreeSelectionStore";
import { useCaseWorkspaceStore } from "@/stores/useCaseWorkspaceStore";
import { cn } from "@/lib/utils";
import { displayLabelForPath } from "@/lib/workspace/derive-display-name";
import { WORKSPACE_FILE_MIME } from "@/lib/workspace/mentions-directive";
import { isUserFacingFile } from "@/lib/workspace/case-files-view";
import { canDeleteSource } from "@/lib/workspace/source-delete";
import { SourceDeleteDialog } from "@/components/shell/SourceDeleteDialog";
import {
  ResourceCopyMoveDialog,
  type CopyMoveSource,
} from "@/components/workspace/ResourceCopyMoveDialog";
import { SendToPicker } from "@/components/workspace/SendToPicker";
import {
  FileDisplayDetailsDialog,
  type FileDisplayNode,
} from "@/components/shell/FileDisplayDetailsDialog";
import { RowContextMenu, type RowContextMenuItem } from "@/components/shell/RowContextMenu";
import {
  useCopyMoveDragStore,
  COPY_MOVE_DRAG_MIME,
} from "@/stores/useCopyMoveDragStore";
import type { WorkspaceTreeFile, WorkspaceTreeFolder } from "@/types/gateway";

export { isUserFacingFile };

/** A `sources/` resource is the only copy/move-eligible entry (the
 *  companion-set carry + reindex matter there). Gate the action on this
 *  exact path prefix — handoff §5. */
export function isSourceResource(path: string): boolean {
  return path.startsWith("sources/");
}

/** Workspace-file-aliases (Part A) — a same-directory sibling for the D3
 *  uniqueness pre-check in the rename popover. `eff` is the node's effective
 *  display name (alias if set, else raw name — matching the runtime's
 *  `effectiveDisplayName`, which does NOT use the folder-map label). */
export interface SiblingEntry {
  path: string;
  eff: string;
}

/** Effective display name of a tree node: a trimmed alias, else the raw name.
 *  (Folder labels + the sibling-uniqueness set still use this alias-based fold.) */
export function effectiveNodeName(node: { name: string; alias?: string }): string {
  return node.alias && node.alias.trim() ? node.alias.trim() : node.name;
}

/**
 * Workspace-file-tags (A8) — the TAG-FIRST tree row lines for a FILE:
 *   - `primary` = the `@tag` (line 1) when tagged, else the title/alias/name;
 *   - `title`   = line 2, the friendly name, shown ONLY when a tag occupies line 1
 *     (so an untagged file degrades to the 2-line title+description row);
 *   - `isTag`   = whether line 1 is a tag handle (rendered in mono).
 * The description is line 3 (rendered by the row).
 */
export type RowLabelMode = "tag-first" | "title-first";

export function fileDisplayLines(
  node: { name: string; alias?: string; tag?: string; title?: string; path?: string },
  /**
   * `title-first` — the D3 ruling (2026-07-31) FOR THE CASE-FILES VIEW ONLY.
   * The friendly name leads and the handle moves to the expanded card, where
   * it does its actual job as a *reference* affordance. Default stays
   * `tag-first` so the classic tree and every other consumer keep A8 behaviour
   * byte-identical.
   */
  mode: RowLabelMode = "tag-first",
): {
  primary: string;
  title?: string;
  isTag: boolean;
} {
  const tag = node.tag?.trim();
  const friendly = node.title?.trim() || node.alias?.trim() || "";
  // ⚖ S1 — the LAST RESORT, and the reason this whole slice exists. Until
  // 2026-08-10 both branches below ended in `node.name`, the raw OS stem, while
  // the comment here claimed they never would. There was no deriver on the user
  // plane to call; now there is, and it is the same rule the agent plane has
  // obeyed for months. See `lib/workspace/derive-display-name.ts`.
  const derived = displayLabelForPath(node.path ?? "", node.name);
  if (mode === "title-first") {
    // A friendly name leads; then the handle exactly as A8 renders it; and
    // failing both, a DERIVED name — never the OS stem.
    if (friendly) return { primary: friendly, isTag: false };
    if (tag) return { primary: tag, isTag: true };
    return { primary: derived, isTag: false };
  }
  // The tag is shown WITHOUT the `@` (operator 2026-07-03) — the mono styling is
  // the "this is the handle" cue; the raw `@` is display noise.
  if (tag) return { primary: tag, title: friendly || undefined, isTag: true };
  return { primary: friendly || derived, isTag: false };
}

/**
 * The label the case-files view DISPLAYS for a file — the single source the
 * alphabetical sort keys on (F7). Sorting on anything the lawyer cannot see
 * reads as broken ordering, so this and the row must never diverge.
 */
export function caseFileLabel(node: {
  name: string;
  alias?: string;
  tag?: string;
  title?: string;
  path?: string;
}): string {
  return fileDisplayLines(node, "title-first").primary;
}

/** Lowercased extension of a path, without the dot. */
function extensionOfPath(path: string): string {
  const dot = path.lastIndexOf(".");
  return dot >= 0 ? path.slice(dot + 1).toLowerCase() : "";
}

/**
 * Extensions whose viewer dispatch lands on the DEFAULT branch — the
 * extraction reader (`ExtractedSourceViewer`). Only these get a "Ver extração"
 * action: a PDF/image/audio has a real document to show, which beats its
 * machine extraction. KEEP IN SYNC with `viewerForExtension` in
 * `components/workspace/viewers.tsx` — anything NOT handled there falls to the
 * default branch, so this set lists the common office/mail binaries rather
 * than trying to enumerate an open world.
 */
const EXTRACTION_VIEWER_EXTS: ReadonlySet<string> = new Set([
  "xlsx", "xls", "pptx", "ppt", "msg", "eml", "odt", "ods", "odp", "rtf", "doc",
]);

/** Room threshold for the collapsed row's inline mtime: only names shorter
 *  than this leave usable space beside them (operator 2026-07-30). */
export const ROW_DATE_NAME_MAX = 30;

/**
 * The collapsed row's SMART mtime (operator 2026-07-30): current year →
 * day + short month ("12 mar"); any other year → month + 2-digit year
 * ("mar-25") — the day stops mattering at that distance, the year starts.
 * Locale-aware short months (pt "mar.", en "Mar") with the trailing dot
 * stripped. Unparseable/absent input renders NOTHING (empty string) — a
 * wrong date is worse than no date. `now` injectable for tests.
 */
export function smartFileDate(iso: string | undefined, locale: string, now: Date = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const month = d.toLocaleDateString(locale, { month: "short" }).replace(/\./g, "");
  if (d.getFullYear() === now.getFullYear()) {
    return `${d.getDate()} ${month}`;
  }
  return `${month}-${String(d.getFullYear() % 100).padStart(2, "0")}`;
}

/**
 * Raw-entity-ID guard — may this `from` value be shown to a lawyer?
 *
 * The platform mints opaque machine ids: `case-<base36 ts>-<base36 rand>`
 * (lexserver `db/models/case-registry.ts`) and `case-<12 hex>` (lx-kernel
 * `models/case.ts`), plus the same shape for `party-` / `org-`. Slugs and
 * display titles exist precisely so those ids stay internal — **none of them may
 * ever reach user-facing copy** (the sibling rule to "never expose the OS
 * filename").
 *
 * `provenance.from` is contractually a *human origin label* ("Trend Casual",
 * "Global" — see `types/gateway.ts`), but the runtime currently stamps the source
 * caseId there for every case→case copy, so the chip reads "DE CASE-MP35NT9R-…".
 * The real fix is a read-time resolver on the runtime side; this predicate is
 * what keeps the id off screen meanwhile, and it STAYS afterwards as defence in
 * depth — a surface that renders whatever it is handed will leak the next id too.
 *
 * Blank counts as not-displayable: an empty label is equally unusable and the
 * caller substitutes the same generic wording.
 *
 * DELIBERATELY BIASED TOWARD SUPPRESSION: the anchored prefix test also rejects
 * a genuine case titled literally "Case-Something". That trade is intentional —
 * a false positive costs one generic phrase, a false negative leaks the exact
 * defect this exists to stop. Do not tighten it to reduce false positives
 * without a stronger id-shape signal.
 *
 * Exported for the colocated unit test.
 */
export function isDisplayableOriginLabel(from: string | undefined): boolean {
  const value = from?.trim() ?? "";
  if (!value) return false;
  return !/^(?:case|party|org|user)-/i.test(value);
}

/**
 * The provenance chip's visible text + tooltip, with the raw-id guard applied to
 * BOTH. The tooltip matters as much as the label: it interpolates the same
 * `from` value, so guarding only the visible text would still leak the id on
 * hover.
 *
 * Exported for the colocated unit test.
 */
export function provenanceChipCopy(
  prov: NonNullable<WorkspaceTreeFile["provenance"]>,
  t: TFunction,
): { short: string; title: string } {
  const promoted = prov.kind === "promoted";
  if (isDisplayableOriginLabel(prov.from)) {
    return {
      short: t(
        promoted
          ? "resourceCopyMove.provenancePromotedShort"
          : "resourceCopyMove.provenanceCopiedShort",
        { from: prov.from },
      ),
      title: t(
        promoted ? "resourceCopyMove.provenancePromoted" : "resourceCopyMove.provenanceCopied",
        { from: prov.from },
      ),
    };
  }
  return {
    short: t(
      promoted
        ? "resourceCopyMove.provenancePromotedShortGeneric"
        : "resourceCopyMove.provenanceCopiedShortGeneric",
    ),
    title: t(
      promoted
        ? "resourceCopyMove.provenancePromotedGeneric"
        : "resourceCopyMove.provenanceCopiedGeneric",
    ),
  };
}

/** All same-directory siblings (folders + user-facing files) with their
 *  effective names — the uniqueness set a child excludes itself from. Uses the
 *  UNFILTERED folder list (client `visible:false` still occupies the name on
 *  disk, so the server counts it). */
export function directorySiblings(folder: WorkspaceTreeFolder): SiblingEntry[] {
  return [
    ...folder.folders.map((f) => ({ path: f.path, eff: effectiveNodeName(f) })),
    ...folder.files
      .filter(isUserFacingFile)
      .map((f) => ({ path: f.path, eff: effectiveNodeName(f) })),
  ];
}

/** The sibling effective-name list a node passes to its rename popover
 *  (excludes the node's own path). */
export function siblingNamesExcluding(siblings: readonly SiblingEntry[], selfPath: string): string[] {
  return siblings.filter((s) => s.path !== selfPath).map((s) => s.eff);
}

/**
 * Map a file extension → {icon, tint}. Long filenames truncate, so the icon is
 * the primary at-a-glance type affordance (operator-flagged 2026-06-26).
 *
 * N1 (navigator lightening): **8 hue families → 5**, all desaturated one step.
 * The VSCode model — colour lives in the 14px glyph, every text row shares one
 * ink — is what lets a dense tree stay quiet. Dropped the blue/purple/amber/
 * orange splits, which encoded distinctions no lawyer makes (.md vs .txt) at the
 * cost of a rainbow. Idle/active are now adjacent ramp steps rather than an
 * opacity dilution, so nothing muddies against the warm canvas.
 *
 *   document (pdf/doc/docx)          → red      (universal convention)
 *   image   (png/jpg/gif/webp/svg)   → green
 *   a/v     (audio + video)          → cyan     ← ONE hue, TWO glyphs
 *   sheet   (csv/xls/xlsx)           → emerald
 *   text    (md/txt/log/json/yml/…)  → neutral
 *   unknown                          → faint neutral + HelpCircle
 *
 * ⚠ Audio vs video stay distinguishable by SHAPE (`AudioLines` — a pure
 * waveform, no file-sheet silhouette — vs `FileVideo`). That was the fix for a
 * real 2026-07-02 complaint (audio read as pdf at 13px in dark mode); merging
 * their hue does not reintroduce it, because the discriminator was never colour.
 */
function fileIconFor(filename: string): { Icon: LucideIcon; idle: string; active: string } {
  const dot = filename.lastIndexOf(".");
  const ext = dot >= 0 ? filename.slice(dot + 1).toLowerCase() : "";
  switch (ext) {
    // ── document ──────────────────────────────────────────────── red
    case "pdf":
    case "docx":
    case "doc":
      return {
        Icon: FileText,
        idle: "text-red-400 dark:text-red-400",
        active: "text-red-500 dark:text-red-300",
      };
    // ── image ───────────────────────────────────────────────── green
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "svg":
      return {
        Icon: FileImage,
        idle: "text-green-400 dark:text-green-400",
        active: "text-green-500 dark:text-green-300",
      };
    // ── sheet ─────────────────────────────────────────────── emerald
    case "csv":
    case "xlsx":
    case "xls":
      return {
        Icon: FileSpreadsheet,
        idle: "text-emerald-400 dark:text-emerald-400",
        active: "text-emerald-500 dark:text-emerald-300",
      };
    // ── audio ────────────────────────────── cyan · waveform glyph ──
    case "mp3":
    case "wav":
    case "m4a":
    case "ogg":
    case "oga":
    case "opus":
    case "flac":
    case "aac":
    case "webm":
      return {
        Icon: AudioLines,
        idle: "text-cyan-400 dark:text-cyan-400",
        active: "text-cyan-500 dark:text-cyan-300",
      };
    // ── video ─────────────────── cyan · film glyph (shape, not hue) ─
    case "mp4":
    case "mov":
    case "avi":
    case "mkv":
      return {
        Icon: FileVideo,
        idle: "text-cyan-400 dark:text-cyan-400",
        active: "text-cyan-500 dark:text-cyan-300",
      };
    // ── text / markup ─────────────────────────────────────── neutral
    case "md":
    case "markdown":
    case "txt":
    case "log":
      return {
        Icon: FileText,
        idle: "text-slate-400 dark:text-slate-500",
        active: "text-slate-600 dark:text-slate-300",
      };
    case "json":
    case "yaml":
    case "yml":
    case "xml":
    case "html":
      return {
        Icon: FileCode,
        idle: "text-slate-400 dark:text-slate-500",
        active: "text-slate-600 dark:text-slate-300",
      };
    default:
      // Unknown / binary — faint neutral so "no preview available" reads as
      // expected rather than broken. `UnsupportedViewer` mirrors the cue.
      return {
        Icon: HelpCircle,
        idle: "text-slate-300 dark:text-slate-600",
        active: "text-slate-500 dark:text-slate-400",
      };
  }
}

/** An action button for the expanded card's single-line bar. Icon-only by
 *  default (accessible name/tooltip = the label, so the bar fits the narrow tree
 *  pane on one line); pass `showLabel` to also render the label as visible text
 *  beside the icon (used for the primary Visualizar). */
function RowActionButton({
  Icon,
  label,
  onClick,
  testid,
  showLabel = false,
}: {
  Icon: LucideIcon;
  label: string;
  onClick: () => void;
  testid?: string;
  /** When set, render the label as text beside the icon (auto width) instead of
   *  the default icon-only h-6 w-6 square. Used for the primary Visualizar. */
  showLabel?: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "flex h-6 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-300/70 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100",
        showLabel ? "gap-1 px-1.5 text-[11px] font-medium" : "w-6",
      )}
    >
      <Icon size={13} className="shrink-0" />
      {showLabel && <span className="truncate">{label}</span>}
    </button>
  );
}

/**
 * "Baixar original" — its own component so `useFileDownload` mounts only while
 * a card is OPEN. Inlined in `FileRow` it would arm one query subscription per
 * row (96 on the dev slice case) for a control at most one row can show.
 */
function DownloadOriginalButton({
  path,
  filename,
  label,
}: {
  path: string;
  filename: string;
  label: string;
}) {
  const download = useFileDownload(path, filename);
  return (
    <RowActionButton
      testid="case-files-action-download"
      Icon={Download}
      label={label}
      onClick={() => download.request()}
    />
  );
}

export function FileRow({
  file,
  copyMoveSource,
  siblings,
  labelMode = "tag-first",
  origin,
  badge,
}: {
  file: WorkspaceTreeFile;
  copyMoveSource: CopyMoveSource | null;
  siblings: readonly SiblingEntry[];
  /** `title-first` inside the case-files view (D3); the classic tree keeps A8. */
  labelMode?: RowLabelMode;
  /** The ORIGINAL's provenance, resolved by the view model's `sourceId` join.
   *  Preferred over this node's own `channel`, which records the intake
   *  harness that WROTE the entry ("agent") rather than how the file arrived. */
  origin?: { channel?: string; uploadedAt?: string };
  /** ⚖ S5 — set on a COMPOUND HEAD to state how many members it holds
   *  ("175 itens"). Absent on an ordinary row, which is the honest default:
   *  a badge reading "0 itens" on every standalone file would be noise, and a
   *  badge that is always present stops carrying information. */
  badge?: string;
}) {
  const { t, i18n } = useTranslation();
  const openFile = useWorkspaceFileStore((s) => s.openFile);
  const activeFile = useWorkspaceFileStore((s) => s.activeFile);
  const selectedPath = useWorkspaceTreeSelectionStore((s) => s.selectedPath);
  const select = useWorkspaceTreeSelectionStore((s) => s.select);
  const noteActivity = useWorkspaceTreeSelectionStore((s) => s.noteActivity);
  const beginDrag = useCopyMoveDragStore((s) => s.begin);
  const endDrag = useCopyMoveDragStore((s) => s.end);
  // Two independent cues (workspace-file-aliases expand-on-select):
  //   isSelected — the row the user single-clicked; drives the highlight +
  //     the in-place EXPANSION (full description + action buttons).
  //   isOpen     — the file currently shown in the viewer pane; drives the
  //     coloured icon tint so "what I'm viewing" stays visible even after the
  //     selection moves to another row.
  const isSelected = selectedPath === file.path;
  const isOpen = activeFile?.path === file.path;
  const { Icon, idle, active } = fileIconFor(file.name);
  const [cmDialog, setCmDialog] = useState<"copy" | "move" | null>(null);
  // Row actions live on a RIGHT-CLICK context menu (not hover) so they never
  // reserve horizontal space / truncate the label (operator brief 2026-07-03).
  // These flags open the surfaces the menu items trigger; each anchors to the
  // row button (rowRef).
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [sendToOpen, setSendToOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  // Acervo delete (2026-08-01) — cascade-resolved case role. ABSENT is never
  // fabricated into a role: an unresolved role hides the affordance exactly as
  // a non-owner does (the store's own docblock forbids the substitution).
  const caseRole = useCaseWorkspaceStore((s) => s.data?.role);

  // TAG-FIRST row lines (A8): line 1 = `@tag` (or the friendly name when untagged),
  // line 2 = the title (when tagged), line 3 = description. The viewer tab + tooltip
  // use the FRIENDLY name (title/alias), never the @tag or the OS name.
  const lines = fileDisplayLines(file, labelMode);
  // Viewer tab title = the best FRIENDLY name, never the `@tag` prefix and never
  // the raw OS name before a friendly option (rule 6): title → alias → tag → name.
  const friendlyName = file.title?.trim() || file.alias?.trim() || file.tag?.trim() || file.name;
  const renameNode: FileDisplayNode = {
    path: file.path,
    isDirectory: false,
    name: file.name,
    alias: file.alias,
    tag: file.tag,
    title: file.title,
    description: file.description,
    // Read-only facts the dialog SHOWS but never edits. `channel`/`uploadedAt`
    // come from the resolved ORIGINAL (the view model's sourceId join), not
    // this entry's own writer stamp — see the `origin` prop.
    size: file.size,
    modifiedAt: file.modifiedAt,
    modality: file.modality,
    originalName: file.originalName,
    channel: origin?.channel ?? file.channel,
    uploadedAt: origin?.uploadedAt ?? file.uploadedAt,
    ingestStatus: file.ingestStatus,
    provenance: file.provenance,
  };

  // Copy/Move is offered only on `sources/` resources (the companion-set
  // carry + reindex matter there) AND only when we can build a valid `from`
  // address (a fully-resolved case selection or the org scope). CCM Wave 3.
  const canCopyMove = copyMoveSource != null && isSourceResource(file.path);

  // ── Acervo delete gate (2026-08-01) ────────────────────────────────────────
  // Four conditions, all in `canDeleteSource`: the ship gate, case scope,
  // the `sources/` floor, and case-OWNER. `copyMoveSource` doubles as the case
  // ADDRESS — its `scope:"case"` arm carries the full triple the RPC needs, and
  // its presence is already the proof that a case scope resolved.
  const caseRef =
    copyMoveSource?.scope === "case"
      ? {
          caseId: copyMoveSource.caseId,
          clientId: copyMoveSource.clientId,
          matterId: copyMoveSource.matterId,
        }
      : null;
  const canDelete =
    caseRef != null &&
    canDeleteSource({ role: caseRole, path: file.path, inCaseScope: true });

  // Origin-chip copy, computed ONCE (the tooltip and the label must agree, and
  // both go through the raw-id guard — see `provenanceChipCopy`).
  const originCopy = file.provenance ? provenanceChipCopy(file.provenance, t) : null;

  // Auto-collapse (operator 2026-07-27) — SUSPEND the idle timer while this row
  // has an overlay open. Those overlays are the user acting on the row, and
  // several render in portals, so without the lock a click inside one reads as
  // "outside the tree" and would collapse the row beneath it.
  const hasOverlay =
    menu !== null || renameOpen || sendToOpen || cmDialog !== null || deleteOpen;
  useEffect(() => {
    if (!hasOverlay) return;
    const { lockOverlay, unlockOverlay } = useWorkspaceTreeSelectionStore.getState();
    lockOverlay();
    return unlockOverlay;
  }, [hasOverlay]);

  // Row tooltip (N2). Collapsed rows show ONE line, so hover is the cheap way to
  // recover the fuller name. Prefer the friendly title; else the shown primary
  // (useful precisely when it truncates). NEVER the raw OS filename (plan §0) —
  // hence the `!== file.name` guard, which yields undefined for an uncurated file.
  const rowTooltip = lines.title ?? (lines.primary !== file.name ? lines.primary : undefined);

  // Collapsed-row inline mtime (see the render note at line 1). Locale from
  // the app's i18n so pt gets pt month abbreviations.
  const rowDate = smartFileDate(file.modifiedAt, i18n.language || "pt-BR");

  // ── Case-files view additions (plan §6 F2). All render only when their datum
  //    exists: absence of a field is silence, never a placeholder.
  //
  // FALHOU (D5): branch on `=== "failed"`, NEVER on `!== "failed"` — an ABSENT
  // `ingestStatus` means the outcome is UNKNOWN (an unreadable companion), and
  // painting unknown as success is a lie a lawyer cannot see through.
  const ingestFailed = file.ingestStatus === "failed";
  // Obsolete (⚠ Reprocessar) is decided by the view model's conjunction; the
  // row only needs to know it landed in that group, which it infers from the
  // one field it can see. Rendered ONLY in title-first (case-files) mode so
  // the classic tree is untouched.
  const isObsoleteRow = labelMode === "title-first" && !file.modality?.trim();
  // Prefer the ORIGINAL's arrival (resolved by the view model's sourceId join)
  // over this entry's own stamp — see the `origin` prop's docblock.
  const originChannel = origin?.channel ?? file.channel;
  const arrivalLabel = originChannel
    ? // OPEN vocabulary: an unrecognised channel renders VERBATIM rather than
      // through a missing key — a closed map over a server vocabulary is this
      // codebase's false-green defect class.
      (t(`caseFiles.channels.${originChannel}`, { defaultValue: originChannel }) as string)
    : null;
  const arrivalDate = smartFileDate(
    origin?.uploadedAt ?? file.uploadedAt,
    i18n.language || "pt-BR",
  );
  // The ORIGINAL's format, taken from the uploader's own filename — that
  // extension is part of the string they typed, not a re-derivation of the
  // server's routing. Falls back to the frozen entry's own extension.
  const originalExt = extensionOfPath(file.originalName ?? file.name);
  // Prefer the uploader's OWN filename: they recognise "ACORDOS Buffet.xlsx",
  // not the generated slug the freeze renamed it to.
  const downloadName = file.originalName ?? file.name;
  const addToast = useToastStore((st) => st.addToast);
  // "Ver extração" is offered only where the viewer's DEFAULT branch (the
  // extraction reader) will handle it — a PDF/image/audio has a real document
  // to show, which beats its machine extraction.
  const canViewExtraction =
    labelMode === "title-first" &&
    !isObsoleteRow &&
    EXTRACTION_VIEWER_EXTS.has(extensionOfPath(file.path));

  async function copyHandle() {
    if (!file.tag) return;
    try {
      await navigator.clipboard.writeText(`@${file.tag}`);
      addToast({ text: t("caseFiles.card.handleCopied"), kind: "success", autoDismissMs: 2500 });
    } catch {
      // A clipboard denied by permissions is not an app error worth a red
      // toast — but it must not silently look like it worked either.
      addToast({ text: t("caseFiles.card.copyHandle"), kind: "error", autoDismissMs: 3000 });
    }
  }

  // Single-click / tap on a COLLAPSED row SELECTS it (expand-on-select,
  // instant) — and, when the viewer pane is already open, SWITCHES it too
  // (2026-08-11; see the block inside). A click on the ALREADY-EXPANDED row —
  // anywhere on the card — opens the CONTENT VIEWER (operator 2026-07-30: the
  // content is what a second click means; details/edit is the pencil's job
  // now). This retires the old deferred-dialog + double-click-disambiguation
  // machinery entirely: no timer, no ROW_CLICK_DELAY_MS race, and a
  // double-click degrades harmlessly to the same viewer.
  //
  // Keyboard parity is automatic: Enter/Space on the row call straight into
  // here, so a keyboard user gets the same one-press switch.
  function handleClick() {
    if (isSelected) {
      handleVisualize();
      return;
    }
    select(file.path);
    // ⚖ When the viewer pane is ALREADY OPEN, the second click is pure friction
    // (operator 2026-08-11). The card's two-phase rhythm exists so that LOOKING
    // at a file does not force you to OPEN it — but once the pane is open that
    // decision is already made, and the user clicking another row is switching
    // what they are reading, not asking whether to read it. So the selection
    // carries the content with it.
    //
    // The card still appears: it is where the action buttons live, and this
    // deliberately does NOT suppress it. Only the second click is removed.
    //
    // Two conditions, both load-bearing:
    //   - pane CLOSED ⇒ unchanged. Auto-opening here would delete the cheap
    //     look the card was built to provide.
    //   - this row ALREADY open ⇒ do not re-open. `openFile` writes a fresh
    //     `activeFile` object, which remounts the viewer and throws away its
    //     scroll position — re-reading a 743-message conversation from the top
    //     because you clicked its own row is a regression, not a refresh.
    if (activeFile !== null && !isOpen) handleVisualize();
  }

  // The "Visualizar" action / details-dialog button / double-click open the
  // viewer for this row (focus returns to the row on close).
  function handleVisualize() {
    openFile({ path: file.path, title: friendlyName }, rowRef.current);
  }

  // Drag SOURCE — serves TWO independent drop targets, each keyed to its own
  // MIME so they never cross-accept:
  //   1. WORKSPACE_FILE_MIME → the composer @-mention drop (handoff
  //      `lexide-mention-workspace-files-handoff-2026-06-29.md` §3.4).
  //   2. COPY_MOVE_DRAG_MIME → the CCM drag-to-destination overlay (§1.1),
  //      added ONLY for a copy/move-eligible `sources/` resource. Starting
  //      the drag also opens the destination overlay via the drag store.
  function handleDragStart(e: React.DragEvent<HTMLDivElement>) {
    // Keep the raw basename here for consistency with the @-mention PICKER
    // (`flattenWorkspaceTree` uses `f.name`); `path` is the machine reference
    // the composer directive carries to the LLM (D9). Alias-labeled mention
    // chips are a deferred follow-up (needs MentionedFile to carry the alias +
    // dual alias/raw-name search in `filterWorkspaceFiles`).
    e.dataTransfer.setData(
      WORKSPACE_FILE_MIME,
      JSON.stringify({ path: file.path, name: file.name }),
    );
    e.dataTransfer.effectAllowed = "copyMove";
    if (canCopyMove && copyMoveSource) {
      // The CCM drag carries the FRIENDLY name for display (the drop overlay +
      // collision confirm show it); the op is keyed on `path`. Never the OS
      // filename (rule 6).
      e.dataTransfer.setData(
        COPY_MOVE_DRAG_MIME,
        JSON.stringify({ path: file.path, name: friendlyName }),
      );
      beginDrag({ path: file.path, name: friendlyName, source: copyMoveSource });
    }
  }

  function handleDragEnd() {
    // The overlay also ends on the global `dragend`, but clearing here too
    // guarantees the overlay tears down even if a drop happened off-target.
    if (canCopyMove) endDrag();
  }

  return (
    <li>
      {/* Expand-on-select CARD: when a row is SELECTED it grows into a card —
          this wrapper carries ONE prominent background/ring around the row,
          the full description AND the icon action bar, so the expanded content
          reads as a single unit. The bordered pen pinned at the card's
          top-right (beside the alias) opens the view/edit dialog; everything
          actionable is rendered OUTSIDE the row <button> (no nested buttons). */}
      <div
        // Interacting with the expanded card restarts its idle countdown, so it
        // is never yanked away from someone reading the description. Throttled
        // in the store — `pointermove` fires continuously.
        onPointerMove={isSelected ? noteActivity : undefined}
        onFocusCapture={isSelected ? noteActivity : undefined}
        className={cn(
          isSelected &&
            "relative my-1 overflow-hidden rounded-lg bg-slate-200/90 shadow-sm ring-1 ring-slate-300 dark:bg-slate-800 dark:ring-slate-600/70",
        )}
      >
        {/* role=button DIV, not <button>: the SELECTED card renders a REAL
            pencil button INLINE beside the name (operator 2026-07-30), and a
            button may not nest a button — the same trade the case-summary
            panel documents. Keyboard parity via tabIndex + Enter/Space. */}
        <div
          role="button"
          tabIndex={0}
          ref={rowRef}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick();
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY });
          }}
          draggable
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          data-testid="case-workspace-nav-doc"
          data-file-path={file.path}
          data-active={isSelected ? "true" : undefined}
          data-open={isOpen ? "true" : undefined}
          aria-current={isSelected ? "page" : undefined}
          // NEVER expose the OS name (plan §0). N2 makes this tooltip carry more
          // weight: collapsed rows are ONE line, so the fuller friendly name is
          // only reachable on hover (or by selecting). Still never the OS name.
          title={rowTooltip}
          // Selected: the card wrapper carries the background; pr-8 reserves
          // the top-right corner for the [X] (the pencil sits inline beside
          // the name now) so the alias never runs under it.
          className={cn(
            "flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 text-left text-[13px] transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40",
            // N3: 22px fixed height when collapsed (VSCode parity) — the single
            // biggest capacity win. The SELECTED row must stay auto-height: it
            // grows into the detail card and a fixed height would clip it.
            isSelected
              ? "py-1 pr-8 font-medium text-slate-900 dark:text-slate-100"
              : "h-[22px] text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800",
          )}
        >
          <Icon size={13} className={cn("shrink-0", isSelected || isOpen ? active : idle)} />
          <span className="flex min-w-0 flex-1 flex-col">
            {/* Line 1 (TAG-FIRST, A8) — the ONLY thing a collapsed row shows: the
                `@tag` in mono when tagged, else the friendly name.
                N2: everything below is gated on `isSelected`. A tree is a place
                to FIND a file, not to read about it; three lines + a chip per row
                cost ~56px and turned a 260px column into a wall. Detail is one
                click away and shows for exactly one row at a time. */}
            {/* Line 1 + the conditional inline mtime (operator 2026-07-30):
                short names leave dead space on the fixed 22px row — fill it
                with the smart date, styled exactly like the folder counts
                (10px, muted, shrink-0, baseline). Gated on name length so a
                long name never fights the date for room, and on the COLLAPSED
                state only — the expanded card has real content there. */}
            <span className="flex min-w-0 items-center gap-1.5">
              <span className={cn("min-w-0 truncate", lines.isTag && "font-mono")}>{lines.primary}</span>
              {badge && (
                <span
                  data-testid="case-files-node-badge"
                  className="ml-1.5 shrink-0 rounded bg-cream-100/80 px-1 py-0.5 text-[9px] font-medium tabular-nums text-slate-500 dark:bg-coffee-800/60 dark:text-slate-400"
                >
                  {badge}
                </span>
              )}
              {/* Inline pencil — NEXT TO the filename (operator 2026-07-30,
                  v2 of the corner pair): details/edit sits where the eye
                  already is; the [X] keeps the corner alone, box-standard.
                  stopPropagation so it never triggers the card's
                  open-the-viewer click. */}
              {isSelected && (
                <button
                  type="button"
                  data-testid="row-action-edit-display"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRenameOpen(true);
                  }}
                  title={t("displayRename.detailsAction")}
                  aria-label={t("displayRename.detailsAction")}
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-slate-400/70 text-slate-500 transition-colors hover:border-slate-500 hover:bg-white/70 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:border-slate-500/70 dark:text-slate-400 dark:hover:border-slate-400 dark:hover:bg-slate-700/80 dark:hover:text-slate-100"
                >
                  <Pencil size={11} />
                </button>
              )}
              {isObsoleteRow && (
                <span
                  data-testid="case-files-stale-badge"
                  title={t("caseFiles.stale.tooltip")}
                  className="flex shrink-0 items-center gap-0.5 rounded bg-amber-100 px-1 text-[9px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                >
                  <AlertTriangle size={9} aria-hidden />
                  {t("caseFiles.stale.badge")}
                </span>
              )}
              {/* ⚠ FALHOU is PER-FILE, from the catalog's `ingestStatus` — it is
                  NOT downstream of `plugin.ingest.state.sources.failed`, and a
                  handshake was once scoped on the reasonable belief that it was
                  (`20260812T210027Z`). One row, one verdict; the event is only
                  a refetch trigger. See `useCaseWorkspaceTree.ts`. */}
              {ingestFailed && labelMode === "title-first" && (
                <span
                  data-testid="case-files-failed-badge"
                  title={t("caseFiles.failedTooltip")}
                  className="shrink-0 rounded bg-red-100 px-1 text-[9px] font-semibold uppercase tracking-wide text-red-700 dark:bg-red-900/40 dark:text-red-300"
                >
                  {t("caseFiles.failedBadge")}
                </span>
              )}
              {!isSelected && rowDate && lines.primary.length < ROW_DATE_NAME_MAX && (
                <span
                  data-testid="row-mtime"
                  className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500"
                >
                  {rowDate}
                </span>
              )}
            </span>
            {isSelected && lines.title && (
              <span className="truncate text-[11px] font-normal text-slate-500 dark:text-slate-400">
                {lines.title}
              </span>
            )}
            {isSelected && labelMode === "title-first" && file.tag && (
              <span className="mt-0.5 flex items-center gap-1">
                <span
                  data-testid="case-files-card-handle"
                  className="truncate font-mono text-[11px] text-slate-600 dark:text-slate-300"
                >
                  @{file.tag}
                </span>
                <button
                  type="button"
                  data-testid="case-files-copy-handle"
                  onClick={(e) => {
                    e.stopPropagation();
                    void copyHandle();
                  }}
                  title={t("caseFiles.card.copyHandle")}
                  aria-label={t("caseFiles.card.copyHandle")}
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-white/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:hover:bg-slate-700/80 dark:hover:text-slate-100"
                >
                  <ClipboardCopy size={10} />
                </button>
              </span>
            )}
            {isSelected && file.description && (
              <span className="mt-0.5 whitespace-pre-wrap break-words text-[11px] text-slate-500 dark:text-slate-400">
                {file.description}
              </span>
            )}
            {/* "enviado: <original filename> · <date>" + the arrival chip (D4).
                The channel is COMPLEMENTARY information — grouping is by
                modality — and its label is the SPA's: `lexide` reads "app",
                because a lawyer never reads the product's internal name. */}
            {isSelected && labelMode === "title-first" &&
              (file.originalName || arrivalLabel || arrivalDate || originalExt) && (
              <span
                data-testid="case-files-card-origin"
                className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-slate-500 [&>span+span]:before:mr-1.5 [&>span+span]:before:content-['·'] dark:text-slate-400"
              >
                {file.originalName && (
                  <span data-testid="case-files-card-original" className="truncate">
                    {t("caseFiles.card.sentAs", { name: file.originalName })}
                  </span>
                )}
                {arrivalLabel && (
                  <span data-testid="case-files-card-channel" data-channel={originChannel}>
                    {t("caseFiles.card.arrivedVia", { channel: arrivalLabel })}
                  </span>
                )}
                {arrivalDate && (
                  <span data-testid="case-files-card-uploaded">{arrivalDate}</span>
                )}
                {originalExt && (
                  <span data-testid="case-files-card-format" className="font-mono uppercase">
                    .{originalExt}
                  </span>
                )}
              </span>
            )}
            {/* Provenance origin chip (§1.5). N2 moved it OFF the collapsed row
                and into the card — it is reference detail, not scanning material.
                Label AND tooltip come from `provenanceChipCopy`, which refuses to
                render a raw entity id and falls back to generic wording. */}
            {isSelected && file.provenance && originCopy && (
              <span
                data-testid="resource-provenance-badge"
                data-provenance-kind={file.provenance.kind}
                data-provenance-named={
                  isDisplayableOriginLabel(file.provenance.from) ? "true" : "false"
                }
                className="mt-1 self-start truncate rounded bg-cream-100/80 px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-500 dark:bg-coffee-800/60 dark:text-slate-400"
                title={originCopy.title}
              >
                {originCopy.short}
              </span>
            )}
          </span>
        </div>

        {/* Pinned card controls (operator 2026-07-30): the [X] takes the
            top-right CORNER — the standard close position for a box — and
            COLLAPSES the card (selection clear; no navigation). The pencil
            moves one slot aside and keeps its job: the view/edit details
            dialog (same as the right-click menu's "Detalhes"). With the
            card's own click now opening the VIEWER, the pencil is the only
            path to details — do not remove it as "redundant". */}
        {isSelected && (
          <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
            <button
              type="button"
              data-testid="row-action-collapse"
              onClick={() => useWorkspaceTreeSelectionStore.getState().clear()}
              title={t("workspaceTree.collapseCard")}
              aria-label={t("workspaceTree.collapseCard")}
              className="flex h-5 w-5 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 border border-slate-400/70 text-slate-500 transition-colors hover:border-slate-500 hover:bg-white/70 hover:text-slate-800 dark:border-slate-500/70 dark:text-slate-400 dark:hover:border-slate-400 dark:hover:bg-slate-700/80 dark:hover:text-slate-100"
            >
              <X size={11} />
            </button>
          </div>
        )}

        {/* Single-line action bar (justify-between): the icon-only sources
            actions (Enviar/Copiar/Mover; label = tooltip/aria name) sit LEFT,
            the primary Visualizar (icon + text) sits RIGHT and opens the content
            viewer. The left group stays present (empty) on non-sources so
            Visualizar keeps its right edge. */}
        {isSelected && (
          <div
            data-testid="case-workspace-nav-doc-actions"
            className="flex items-center justify-between gap-1 border-t border-slate-300/70 px-1.5 py-0.5 dark:border-slate-600/50"
          >
            <div className="flex items-center gap-1">
              {canCopyMove && (
                <>
                  <RowActionButton
                    testid="row-action-send-to"
                    Icon={CornerUpRight}
                    label={t("resourceCopyMove.sendToAction")}
                    onClick={() => setSendToOpen(true)}
                  />
                  <RowActionButton
                    testid="row-action-copy"
                    Icon={Copy}
                    label={t("resourceCopyMove.copyAction")}
                    onClick={() => setCmDialog("copy")}
                  />
                  <RowActionButton
                    testid="row-action-move"
                    Icon={MoveRight}
                    label={t("resourceCopyMove.moveAction")}
                    onClick={() => setCmDialog("move")}
                  />
                </>
              )}
              {/* Delete lives in the LEFT group, as far from the primary
                  Visualizar as the bar allows — a destructive control adjacent
                  to the one the lawyer reaches for constantly is the misclick
                  the confirm exists to catch, and distance is cheaper than a
                  second guard. Red on hover only, so the idle bar stays quiet. */}
              {canDelete && (
                <button
                  type="button"
                  data-testid="case-files-action-delete"
                  onClick={() => setDeleteOpen(true)}
                  title={t("caseFiles.delete.action")}
                  aria-label={t("caseFiles.delete.action")}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-red-100 hover:text-red-700 dark:text-slate-400 dark:hover:bg-red-950/50 dark:hover:text-red-300"
                >
                  <Trash2 size={13} className="shrink-0" />
                </button>
              )}
            </div>
              {labelMode === "title-first" && (
                <DownloadOriginalButton
                  path={file.path}
                  filename={downloadName}
                  label={t("caseFiles.card.downloadOriginal")}
                />
              )}
              {canViewExtraction && (
                <RowActionButton
                  testid="case-files-action-extraction"
                  Icon={FileSearch}
                  label={t("caseFiles.card.viewExtraction")}
                  onClick={() =>
                    openFile(
                      { path: file.path, title: friendlyName, intent: "extraction" },
                      rowRef.current,
                    )
                  }
                />
              )}
            <RowActionButton
              testid="row-action-visualize"
              Icon={Eye}
              label={t("displayRename.visualizeAction")}
              onClick={handleVisualize}
              showLabel
            />
          </div>
        )}
      </div>

      {/* Right-click actions menu. Rename on every file; copy/move on
          `sources/` resources. */}
      {menu && (
        <RowContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            {
              key: "visualize",
              label: t("displayRename.visualizeAction"),
              Icon: Eye,
              onSelect: handleVisualize,
            },
            {
              key: "rename",
              label: t("displayRename.detailsAction"),
              Icon: Pencil,
              onSelect: () => setRenameOpen(true),
            },
            ...(canCopyMove
              ? ([
                  {
                    key: "send-to",
                    label: t("resourceCopyMove.sendToAction"),
                    Icon: CornerUpRight,
                    onSelect: () => setSendToOpen(true),
                  },
                  {
                    key: "copy",
                    label: t("resourceCopyMove.copyAction"),
                    Icon: Copy,
                    onSelect: () => setCmDialog("copy"),
                  },
                  {
                    key: "move",
                    label: t("resourceCopyMove.moveAction"),
                    Icon: MoveRight,
                    onSelect: () => setCmDialog("move"),
                  },
                ] satisfies RowContextMenuItem[])
              : []),
            // LAST, and `danger` (red + a rule above it) so it never sits
            // shoulder-to-shoulder with Copiar/Mover looking the same.
            ...(canDelete
              ? ([
                  {
                    key: "delete",
                    label: t("caseFiles.delete.action"),
                    Icon: Trash2,
                    danger: true,
                    onSelect: () => setDeleteOpen(true),
                  },
                ] satisfies RowContextMenuItem[])
              : []),
          ]}
        />
      )}

      {renameOpen && (
        <FileDisplayDetailsDialog
          node={renameNode}
          siblingEffectiveNames={siblingNamesExcluding(siblings, file.path)}
          onClose={() => setRenameOpen(false)}
          onVisualize={() => {
            setRenameOpen(false);
            handleVisualize();
          }}
        />
      )}

      {canCopyMove && sendToOpen && copyMoveSource && rowRef.current && (
        <SendToPicker
          anchor={rowRef.current}
          path={file.path}
          displayName={friendlyName}
          tag={file.tag}
          source={copyMoveSource}
          onClose={() => setSendToOpen(false)}
        />
      )}

      {canDelete && deleteOpen && caseRef && (
        <SourceDeleteDialog
          path={file.path}
          displayName={friendlyName}
          tag={file.tag}
          caseRef={caseRef}
          onClose={() => setDeleteOpen(false)}
        />
      )}

      {canCopyMove && cmDialog && copyMoveSource && (
        <ResourceCopyMoveDialog
          open
          mode={cmDialog}
          path={file.path}
          displayName={friendlyName}
          tag={file.tag}
          source={copyMoveSource}
          onClose={() => setCmDialog(null)}
        />
      )}
    </li>
  );
}
